// src/components/widget/Widget.tsx
// Rediseño corporativo estilo Notion/Linear
import { useEffect, useState, useCallback, useContext } from "react";
import { Box, Typography, Stack, CircularProgress, alpha } from "@mui/material";
import { PointOfSale, PendingActions, AssignmentReturn, AccountBalanceWalletOutlined, TrendingUp, TrendingDown } from "@mui/icons-material";
import { WidgetServiceApi } from "../../services/api/widgetServiceApi";
import { WidgetProps, WidgetData, WidgetMetrics } from "../../components/types/widget.types";
import { DarkModeContext } from "../../context/darkModeContext";

// Paleta alineada al tema corporativo (navy + azul). Colores semanticos por KPI
// para escaneo rapido; el antiguo morado de "cotizaciones" pasa a cian.
const P = {
  light: { bg: "#FFFFFF", bgS: "#F8FAFC", text: "#0F172A", textS: "#475569", textT: "#64748B", border: "#E2E8F0", accent: "#0369A1", accentBg: "#E0F2FE", success: "#15803D", successBg: "#DCFCE7", warning: "#B45309", warningBg: "#FEF3C7", error: "#DC2626", errorBg: "#FEE2E2", purple: "#0891B2", purpleBg: "#CFFAFE", shadow: "0 1px 2px rgba(15,23,42,0.06), 0 1px 3px rgba(15,23,42,0.04)" },
  dark: { bg: "#0F172A", bgS: "#111827", text: "#E2E8F0", textS: "#94A3B8", textT: "#64748B", border: "#1E293B", accent: "#38BDF8", accentBg: "#0C4A6E", success: "#4ADE80", successBg: "#14352A", warning: "#FBBF24", warningBg: "#3A2E12", error: "#F87171", errorBg: "#3D2020", purple: "#22D3EE", purpleBg: "#164E63", shadow: "0 1px 2px rgba(0,0,0,0.5)" },
};

const widgetConfigs: Record<string, { title: string; subtitle: string; colorKey: string; icon: React.ReactElement }> = {
  ventas: { title: "VENTAS DEL DÍA", subtitle: "Transacciones completadas", colorKey: "accent", icon: <PointOfSale /> },
  pendientes: { title: "FACTURAS PENDIENTES", subtitle: "Pendientes de pago", colorKey: "warning", icon: <PendingActions /> },
  devoluciones: { title: "DEVOLUCIONES DEL MES", subtitle: "Últimos 30 días", colorKey: "success", icon: <AssignmentReturn /> },
  balance: { title: "COTIZACIONES DEL MES", subtitle: "Cotizaciones activas", colorKey: "purple", icon: <AccountBalanceWalletOutlined /> },
};

const Widget: React.FC<WidgetProps> = ({ type }) => {
  const [metrics, setMetrics] = useState<WidgetMetrics>({ cantidad: 0, diferencia: 0 });
  const [loading, setLoading] = useState(true);
  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? P.dark : P.light;

  const fetchMetrics = useCallback(async () => {
    try {
      let result: WidgetMetrics;
      switch (type) {
        case "ventas": result = await WidgetServiceApi.getVentasMetrics(); break;
        case "pendientes": result = await WidgetServiceApi.getFacturasPendientesMetrics(); break;
        case "devoluciones": result = await WidgetServiceApi.getDevolucionesMetrics(); break;
        case "balance": result = await WidgetServiceApi.getBalanceMetrics(); break;
        default: result = { cantidad: 0, diferencia: 0 };
      }
      setMetrics(result); setLoading(false);
    } catch { setMetrics({ cantidad: 0, diferencia: 0 }); setLoading(false); }
  }, [type]);

  useEffect(() => {
    let mounted = true;
    const load = async () => { if (mounted) await fetchMetrics(); };
    load();
    const iv = setInterval(load, 5 * 60 * 1000);
    return () => { mounted = false; clearInterval(iv); };
  }, [fetchMetrics]);

  const cfg = widgetConfigs[type] || widgetConfigs.ventas;
  const color = (p as any)[cfg.colorKey] || p.accent;
  const colorBg = (p as any)[cfg.colorKey + "Bg"] || p.accentBg;
  const isPositive = metrics.diferencia >= 0;

  if (loading) return (
    <Box sx={{ p: 3, borderRadius: "12px", border: `1px solid ${p.border}`, bgcolor: p.bg, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 140 }}>
      <CircularProgress size={24} sx={{ color: p.accent }} />
    </Box>
  );

  return (
    <Box sx={{ p: 2.5, borderRadius: "12px", border: `1px solid ${p.border}`, bgcolor: p.bg, boxShadow: p.shadow, transition: "all 0.2s", "&:hover": { borderColor: color, boxShadow: darkMode ? `0 4px 12px ${alpha(color, 0.15)}` : `0 4px 12px ${alpha(color, 0.1)}` } }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: "8px", bgcolor: colorBg, display: "flex", alignItems: "center", justifyContent: "center", color, "& svg": { fontSize: 18 } }}>
          {cfg.icon}
        </Box>
        <Box>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", letterSpacing: "0.04em" }}>{cfg.title}</Typography>
          <Typography sx={{ fontSize: "0.68rem", color: p.textT }}>{cfg.subtitle}</Typography>
        </Box>
      </Stack>

      {/* Valor */}
      <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: p.text, fontFamily: "'IBM Plex Mono', monospace", mb: 1.5 }}>
        {metrics.cantidad.toLocaleString()}
      </Typography>

      {/* Diferencia */}
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.3, px: 1, py: 0.2, borderRadius: "6px", bgcolor: isPositive ? p.successBg : p.errorBg, color: isPositive ? p.success : p.error, fontSize: "0.75rem", fontWeight: 600 }}>
          {isPositive ? <TrendingUp sx={{ fontSize: 14 }} /> : <TrendingDown sx={{ fontSize: 14 }} />}
          {Math.abs(metrics.diferencia)}%
        </Box>
        <Typography sx={{ fontSize: "0.72rem", color: p.textT }}>vs. período anterior</Typography>
      </Stack>
    </Box>
  );
};

export default Widget;
